export interface PublicChatOption {
  label: string;
  icon?: string;
  next?: string;
  url?: string;       // external / internal href link
  action?: 'contact' | 'pricing' | 'register' | 'login';
  setLang?: 'en' | 'ta';
}

export interface PublicChatNode {
  message: string;
  options: PublicChatOption[];
}

export type PublicChatConfig = Record<string, PublicChatNode>;

export type ChatbotLang = 'en' | 'ta';

const englishFlows: PublicChatConfig = {

  language_select: {
    message: 'Welcome to DriveMynt! 🙏\nPlease choose your preferred language:\n\nDriveMynt-க்கு வரவேற்கிறோம்! 🙏\nஉங்கள் விருப்பமான மொழியைத் தேர்ந்தெடுக்கவும்:',
    options: [
      { label: 'English', icon: '🇬🇧', next: 'start', setLang: 'en' },
      { label: 'தமிழ்', icon: '🇮🇳', next: 'start', setLang: 'ta' },
    ],
  },
  // ─── Entry ───────────────────────────────────────────────────────────────
  start: {
    message: "Hi there! 👋 Welcome to DriveMynt.\n\nI'm here to help you learn about our platform. What would you like to know?",
    options: [
      { label: 'What is DriveMynt?', icon: '🚗', next: 'what_is' },
      { label: 'Features & modules', icon: '✨', next: 'features' },
      { label: 'Pricing & plans', icon: '💰', next: 'pricing' },
      { label: 'How to get started?', icon: '🚀', next: 'get_started' },
      { label: 'Is there a free trial?', icon: '🆓', next: 'free_trial' },
      { label: 'Mobile app available?', icon: '📱', next: 'mobile_app' },
      { label: 'Contact / Support', icon: '📞', next: 'contact' },
      { label: 'Change Language', icon: '🌐', next: 'language_select' },
    ],
  },

  // ─── What is DriveMynt ───────────────────────────────────────────────────
  what_is: {
    message: "DriveMynt is an all-in-one driving school management platform.\n\nIt helps you:\n\n✅ Manage student enrollments & documents\n✅ Schedule training batches & track attendance\n✅ Manage your vehicle fleet\n✅ Track fees & expenses\n✅ Manage staff & instructors\n✅ Access real-time dashboard insights\n\nEverything in one place — no more spreadsheets!",
    options: [
      { label: 'See all features', icon: '✨', next: 'features' },
      { label: 'How to get started?', icon: '🚀', next: 'get_started' },
      { label: 'View pricing', icon: '💰', next: 'pricing' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },

  // ─── Features ────────────────────────────────────────────────────────────
  features: {
    message: 'DriveMynt includes powerful modules for every part of your driving school:',
    options: [
      { label: 'Enrollment management', icon: '📝', next: 'feat_enrollment' },
      { label: 'Batch scheduling', icon: '📅', next: 'feat_batches' },
      { label: 'Vehicle management', icon: '🚗', next: 'feat_vehicles' },
      { label: 'Fee & expense tracking', icon: '💳', next: 'feat_accounting' },
      { label: 'Staff & user roles', icon: '👥', next: 'feat_users' },
      { label: 'Dashboard & reports', icon: '📊', next: 'feat_dashboard' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },
  feat_enrollment: {
    message: "📝 Enrollment Management\n\nEasily register students and track their entire journey:\n\n• Capture student details & documents in one place\n• Link enrollments to services and categories (LMV, HMV, etc.)\n• Track status — Pending, Active, Completed, Cancelled\n• Search and filter by name, status, or date",
    options: [
      { label: 'Other features', icon: '✨', next: 'features' },
      { label: 'Get started free', icon: '🚀', next: 'get_started' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },
  feat_batches: {
    message: "📅 Batch Scheduling\n\nOrganize your training sessions with ease:\n\n• Schedule On-Road and Simulator batches\n• Assign instructors and group students together\n• Set start/end times and track session progress\n• Filter batches by status — Active, Completed, Inactive",
    options: [
      { label: 'Other features', icon: '✨', next: 'features' },
      { label: 'Get started free', icon: '🚀', next: 'get_started' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },
  feat_vehicles: {
    message: "🚗 Vehicle Management\n\nKeep your entire fleet organized:\n\n• Register vehicles with registration number, type & model\n• Track status — Available, In Use, Under Maintenance\n• Assign vehicles to branches\n• View vehicle history and batch assignments",
    options: [
      { label: 'Other features', icon: '✨', next: 'features' },
      { label: 'Get started free', icon: '🚀', next: 'get_started' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },
  feat_accounting: {
    message: "💳 Fee & Expense Tracking\n\nStay on top of your school's finances:\n\n• Record and categorize school expenses\n• Track student fee payments — Paid, Pending, Overdue\n• Export reports as PDF or Excel\n• Get a revenue vs expense summary on the dashboard",
    options: [
      { label: 'Other features', icon: '✨', next: 'features' },
      { label: 'Get started free', icon: '🚀', next: 'get_started' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },
  feat_users: {
    message: "👥 Staff & User Roles\n\nControl who can access what:\n\n• Role-based access — Super Admin, Admin, Staff, Instructor, Student\n• Invite staff via email — they accept and get instant access\n• Each role sees only what they need\n• Manage multiple branches with separate user assignments",
    options: [
      { label: 'Other features', icon: '✨', next: 'features' },
      { label: 'Get started free', icon: '🚀', next: 'get_started' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },
  feat_dashboard: {
    message: "📊 Dashboard & Reports\n\nGet real-time insights at a glance:\n\n• Active students, revenue, and enrollment trends\n• Filter by current branch, overall, or all tenants\n• Batch status overview and recent activity feed\n• Export data for offline reporting",
    options: [
      { label: 'Other features', icon: '✨', next: 'features' },
      { label: 'Get started free', icon: '🚀', next: 'get_started' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },

  // ─── Pricing ─────────────────────────────────────────────────────────────
  pricing: {
    message: "DriveMynt offers flexible plans for every school size:\n\n🆓 Free Trial — 14 days, 50 students, no credit card\n⭐ Standard — Up to 50 students, 1 branch\n🚀 Professional — Up to 500 students, multi-branch\n🏢 Enterprise — Unlimited students & branches\n\n💡 Save up to 20% with yearly billing!\n\nWant help choosing the right plan?",
    options: [
      { label: 'Help me choose a plan', icon: '🎯', next: 'pricing_recommend' },
      { label: 'Free Trial details', icon: '🆓', next: 'free_trial' },
      { label: 'Standard plan', icon: '⭐', next: 'plan_standard' },
      { label: 'Professional plan', icon: '🚀', next: 'plan_professional' },
      { label: 'Enterprise plan', icon: '🏢', next: 'plan_enterprise' },
      { label: 'See full pricing page', icon: '💳', action: 'pricing' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },
  pricing_recommend: {
    message: "Let me help you find the right plan!\n\nHow many active students does your driving school have?",
    options: [
      { label: 'Just starting (< 50)', icon: '🌱', next: 'rec_small' },
      { label: 'Growing (50–500)', icon: '📈', next: 'rec_medium' },
      { label: 'Large school (500+)', icon: '🏫', next: 'rec_large' },
      { label: 'Back', icon: '←', next: 'pricing' },
    ],
  },
  rec_small: {
    message: "For a small school (under 50 students) we recommend:\n\n⭐ Standard Plan\n• All core modules included\n• 1 branch, up to 50 students\n• Most affordable entry plan\n\n💡 Start with the Free Trial first — no credit card needed!",
    options: [
      { label: 'Standard plan details', icon: '⭐', next: 'plan_standard' },
      { label: 'Start free trial', icon: '🆓', action: 'register' },
      { label: 'See pricing page', icon: '💳', action: 'pricing' },
      { label: 'Back', icon: '←', next: 'pricing_recommend' },
    ],
  },
  rec_medium: {
    message: "For a growing school (50–500 students) we recommend:\n\n🚀 Professional Plan\n• Up to 500 students\n• Multi-branch support\n• Advanced analytics & export reports\n• Instructor performance tracking\n\n💡 Yearly billing saves ~20% vs monthly.",
    options: [
      { label: 'Professional plan details', icon: '🚀', next: 'plan_professional' },
      { label: 'Start free trial', icon: '🆓', action: 'register' },
      { label: 'See pricing page', icon: '💳', action: 'pricing' },
      { label: 'Back', icon: '←', next: 'pricing_recommend' },
    ],
  },
  rec_large: {
    message: "For a large school (500+ students) we recommend:\n\n🏢 Enterprise Plan\n• Unlimited students & branches\n• Priority customer support\n• Dedicated onboarding assistance\n\n📩 Contact us for a custom quote.",
    options: [
      { label: 'Enterprise plan details', icon: '🏢', next: 'plan_enterprise' },
      { label: 'Contact us', icon: '📞', next: 'contact' },
      { label: 'See pricing page', icon: '💳', action: 'pricing' },
      { label: 'Back', icon: '←', next: 'pricing_recommend' },
    ],
  },
  plan_standard: {
    message: "⭐ Standard Plan\n\n✅ Students: Up to 50 active\n✅ Branches: 1\n✅ Full access to all core modules\n✅ Enrollment & fee management\n✅ Vehicle & batch scheduling\n✅ Email support\n\n💰 Monthly & Yearly billing available\n💡 Best for: Small driving schools getting started.",
    options: [
      { label: 'Start free trial', icon: '🆓', action: 'register' },
      { label: 'See pricing page', icon: '💳', action: 'pricing' },
      { label: 'Compare all plans', icon: '📊', next: 'pricing' },
      { label: 'Back', icon: '←', next: 'pricing' },
    ],
  },
  plan_professional: {
    message: "🚀 Professional Plan\n\n✅ Students: Up to 500 active\n✅ Branches: Multiple\n✅ All Standard features +\n✅ Advanced analytics & reports\n✅ CSV / PDF exports\n✅ Multi-branch management\n✅ Instructor performance tracking\n✅ Priority email support\n\n💰 Yearly billing = ~20% savings",
    options: [
      { label: 'Start free trial', icon: '🆓', action: 'register' },
      { label: 'See pricing page', icon: '💳', action: 'pricing' },
      { label: 'Compare all plans', icon: '📊', next: 'pricing' },
      { label: 'Back', icon: '←', next: 'pricing' },
    ],
  },
  plan_enterprise: {
    message: "🏢 Enterprise Plan\n\n✅ Students: Unlimited\n✅ Branches: Unlimited\n✅ All Professional features +\n✅ Dedicated account manager\n✅ Advanced role permissions\n✅ White-label options\n✅ Phone & chat priority support\n✅ Custom onboarding & training",
    options: [
      { label: 'Contact us for a quote', icon: '📞', next: 'contact' },
      { label: 'See pricing page', icon: '💳', action: 'pricing' },
      { label: 'Back', icon: '←', next: 'pricing' },
    ],
  },

  // ─── Free Trial ──────────────────────────────────────────────────────────
  free_trial: {
    message: "🆓 Free Trial\n\n✅ Duration: 14 days from activation\n✅ Students: Up to 50\n✅ Branches: 1\n✅ Includes ALL core modules — Enrollment, Vehicles, Batches, Services, Accounting\n✅ No credit card required\n\n⚠️ After the trial your data is safe — just subscribe to continue.",
    options: [
      { label: 'Start free trial now', icon: '🚀', action: 'register' },
      { label: 'Compare paid plans', icon: '📊', next: 'pricing' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },

  // ─── Get Started ─────────────────────────────────────────────────────────
  get_started: {
    message: "Getting started with DriveMynt is easy:\n\n1️⃣ Register your school — takes 2 minutes\n2️⃣ Start your 14-day free trial automatically\n3️⃣ Add your services, vehicles & staff\n4️⃣ Start enrolling students right away!\n\nNo setup fees. No credit card required.",
    options: [
      { label: 'Register now — it\'s free', icon: '🚀', action: 'register' },
      { label: 'I already have an account', icon: '🔑', action: 'login' },
      { label: 'Tell me about pricing', icon: '💰', next: 'pricing' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },

  // ─── Mobile App ──────────────────────────────────────────────────────────
  mobile_app: {
    message: "📱 DriveMynt Mobile App\n\nYes! DriveMynt has a mobile app for students and instructors:\n\n• Students can view their schedule, track sessions & check enrollment status\n• Instructors can manage their batches on the go\n• Real-time GPS location tracking for on-road sessions\n• Available for Android & iOS",
    options: [
      { label: 'Get started', icon: '🚀', next: 'get_started' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },

  // ─── Contact / Support ───────────────────────────────────────────────────
  contact: {
    message: "📞 Get in Touch\n\nOur team is happy to help you!\n\n📧 Email: contact@auxwit.com\n\nOr use the Contact form on our website — we typically respond within 1 business day.\n\nFor sales enquiries or a demo, feel free to reach out!",
    options: [
      { label: 'Go to contact form', icon: '✉️', action: 'contact' },
      { label: 'See pricing', icon: '💰', next: 'pricing' },
      { label: 'Start free trial', icon: '🚀', action: 'register' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },

  // ─── FAQ ─────────────────────────────────────────────────────────────────
  faq: {
    message: "Here are some frequently asked questions. Which one would you like answered?",
    options: [
      { label: 'Is my data secure?', icon: '🔒', next: 'faq_security' },
      { label: 'Can I manage multiple branches?', icon: '🌿', next: 'faq_branches' },
      { label: 'What documents are supported?', icon: '📄', next: 'faq_documents' },
      { label: 'Can I export reports?', icon: '📥', next: 'faq_export' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },
  faq_security: {
    message: "🔒 Data Security\n\nYour data is fully secured:\n\n• Data is encrypted at rest and in transit\n• Each school's data is isolated (multi-tenant architecture)\n• Regular automated backups\n• Secure authentication with OTP verification",
    options: [
      { label: 'More questions', icon: '❓', next: 'faq' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },
  faq_branches: {
    message: "🌿 Multiple Branches\n\nYes! DriveMynt supports multi-branch management:\n\n• Enable branch mode in Settings\n• Add unlimited branches (Professional & Enterprise plans)\n• Assign separate staff, vehicles & students per branch\n• Switch between branches from the header\n• Consolidated reports across all branches",
    options: [
      { label: 'See plans', icon: '💰', next: 'pricing' },
      { label: 'More questions', icon: '❓', next: 'faq' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },
  faq_documents: {
    message: "📄 Document Management\n\nDriveMynt supports uploading and tracking required documents per enrollment:\n\n• Aadhar card, licence copy, passport photo & more\n• Documents are linked to each student's enrollment\n• Easy to review during verification\n• Configurable per service/category",
    options: [
      { label: 'More questions', icon: '❓', next: 'faq' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },
  faq_export: {
    message: "📥 Export & Reports\n\nYes, DriveMynt supports data exports:\n\n• Export fee reports as PDF or Excel\n• Export enrollment and student lists\n• Available on Professional & Enterprise plans\n• Download directly from the Accounting or Enrollment pages",
    options: [
      { label: 'See plans', icon: '💰', next: 'pricing' },
      { label: 'More questions', icon: '❓', next: 'faq' },
      { label: 'Back to menu', icon: '←', next: 'start' },
    ],
  },
};

// ─── Tamil Translations ──────────────────────────────────────────────────────
const tamilFlows: PublicChatConfig = {
  language_select: {
    message: 'Welcome to DriveMynt! 🙏\nPlease choose your preferred language:\n\nDriveMynt-க்கு வரவேற்கிறோம்! 🙏\nஉங்கள் விருப்பமான மொழியைத் தேர்ந்தெடுக்கவும்:',
    options: [
      { label: 'English', icon: '🇬🇧', next: 'start', setLang: 'en' },
      { label: 'தமிழ்', icon: '🇮🇳', next: 'start_ta', setLang: 'ta' },
    ],
  },
  start: {
    message: "வணக்கம்! 👋 DriveMynt-க்கு வரவேற்கிறோம்.\n\nஎங்கள் தளம் பற்றி அறிய நான் இங்கே இருக்கிறேன். நீங்கள் என்ன தெரிந்துகொள்ள விரும்புகிறீர்கள்?",
    options: [
      { label: 'DriveMynt என்றால் என்ன?', icon: '🚗', next: 'what_is' },
      { label: 'அம்சங்கள் & தொகுதிகள்', icon: '✨', next: 'features' },
      { label: 'விலை & திட்டங்கள்', icon: '💰', next: 'pricing' },
      { label: 'எப்படி தொடங்குவது?', icon: '🚀', next: 'get_started' },
      { label: 'இலவச சோதனை உள்ளதா?', icon: '🆓', next: 'free_trial' },
      { label: 'மொபைல் ஆப் உள்ளதா?', icon: '📱', next: 'mobile_app' },
      { label: 'தொடர்பு / ஆதரவு', icon: '📞', next: 'contact' },
      { label: 'மொழி மாற்றவும்', icon: '🌐', next: 'language_select' },
    ],
  },

  what_is: {
    message: "DriveMynt என்பது ஒரு முழுமையான டிரைவிங் பள்ளி மேலாண்மை தளம்.\n\nஇது உங்களுக்கு உதவுகிறது:\n\n✅ மாணவர் சேர்க்கை & ஆவணங்களை நிர்வகிக்க\n✅ பயிற்சி தொகுதிகளை திட்டமிட்டு வருகையைக் கண்காணிக்க\n✅ உங்கள் வாகன வரிசையை நிர்வகிக்க\n✅ கட்டணங்கள் & செலவுகளைக் கண்காணிக்க\n✅ ஊழியர்கள் & பயிற்சியாளர்களை நிர்வகிக்க\n✅ நிகழ்நேர டாஷ்போர்டு நுண்ணறிவுகளை அணுகுக\n\nஎல்லாம் ஒரே இடத்தில் — ஸ்ப்ரெட்ஷீட்கள் தேவையில்லை!",
    options: [
      { label: 'அனைத்து அம்சங்களையும் பார்க்க', icon: '✨', next: 'features' },
      { label: 'எப்படி தொடங்குவது?', icon: '🚀', next: 'get_started' },
      { label: 'விலையைப் பார்க்க', icon: '💰', next: 'pricing' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },

  features: {
    message: 'DriveMynt உங்கள் டிரைவிங் பள்ளியின் ஒவ்வொரு பகுதிக்கும் சக்திவாய்ந்த தொகுதிகளை உள்ளடக்கியது:',
    options: [
      { label: 'சேர்க்கை மேலாண்மை', icon: '📝', next: 'feat_enrollment' },
      { label: 'தொகுதி திட்டமிடல்', icon: '📅', next: 'feat_batches' },
      { label: 'வாகன மேலாண்மை', icon: '🚗', next: 'feat_vehicles' },
      { label: 'கட்டணம் & செலவு கண்காணிப்பு', icon: '💳', next: 'feat_accounting' },
      { label: 'ஊழியர் & பயனர் பாத்திரங்கள்', icon: '👥', next: 'feat_users' },
      { label: 'டாஷ்போர்டு & அறிக்கைகள்', icon: '📊', next: 'feat_dashboard' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },
  feat_enrollment: {
    message: "📝 சேர்க்கை மேலாண்மை\n\nமாணவர்களை எளிதாக பதிவு செய்து அவர்களின் முழு பயணத்தையும் கண்காணிக்கவும்:\n\n• மாணவர் விவரங்கள் & ஆவணங்களை ஒரே இடத்தில் சேகரிக்கவும்\n• சேவைகள் மற்றும் வகைகளுடன் சேர்க்கைகளை இணைக்கவும் (LMV, HMV போன்றவை)\n• நிலையைக் கண்காணிக்கவும் — நிலுவை, செயலில், நிறைவு, ரத்து\n• பெயர், நிலை அல்லது தேதி மூலம் தேடவும்",
    options: [
      { label: 'மற்ற அம்சங்கள்', icon: '✨', next: 'features' },
      { label: 'இலவசமாக தொடங்கு', icon: '🚀', next: 'get_started' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },
  feat_batches: {
    message: "📅 தொகுதி திட்டமிடல்\n\nஉங்கள் பயிற்சி அமர்வுகளை எளிதாக ஒழுங்கமைக்கவும்:\n\n• சாலையில் மற்றும் சிமுலேட்டர் தொகுதிகளை திட்டமிடவும்\n• பயிற்சியாளர்களை நியமிக்கவும் மற்றும் மாணவர்களை குழுவாக்கவும்\n• தொடக்க/முடிவு நேரங்களை அமைத்து அமர்வு முன்னேற்றத்தைக் கண்காணிக்கவும்\n• நிலை மூலம் தொகுதிகளை வடிகட்டவும் — செயலில், நிறைவு, செயலற்ற",
    options: [
      { label: 'மற்ற அம்சங்கள்', icon: '✨', next: 'features' },
      { label: 'இலவசமாக தொடங்கு', icon: '🚀', next: 'get_started' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },
  feat_vehicles: {
    message: "🚗 வாகன மேலாண்மை\n\nஉங்கள் முழு வாகன வரிசையையும் ஒழுங்கமைக்கவும்:\n\n• பதிவு எண், வகை & மாடல் உடன் வாகனங்களைப் பதிவு செய்யவும்\n• நிலையைக் கண்காணிக்கவும் — கிடைக்கும், பயன்பாட்டில், பராமரிப்பில்\n• கிளைகளுக்கு வாகனங்களை ஒதுக்கவும்\n• வாகன வரலாறு மற்றும் தொகுதி ஒதுக்கீடுகளைப் பார்க்கவும்",
    options: [
      { label: 'மற்ற அம்சங்கள்', icon: '✨', next: 'features' },
      { label: 'இலவசமாக தொடங்கு', icon: '🚀', next: 'get_started' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },
  feat_accounting: {
    message: "💳 கட்டணம் & செலவு கண்காணிப்பு\n\nஉங்கள் பள்ளியின் நிதிகளை மேற்பார்வையிடுங்கள்:\n\n• பள்ளி செலவுகளைப் பதிவு செய்து வகைப்படுத்தவும்\n• மாணவர் கட்டண செலுத்தல்களைக் கண்காணிக்கவும் — செலுத்தப்பட்டது, நிலுவை, தாமதம்\n• PDF அல்லது Excel ஆக அறிக்கைகளை ஏற்றுமதி செய்யவும்\n• டாஷ்போர்டில் வருவாய் vs செலவு சுருக்கம் பெறவும்",
    options: [
      { label: 'மற்ற அம்சங்கள்', icon: '✨', next: 'features' },
      { label: 'இலவசமாக தொடங்கு', icon: '🚀', next: 'get_started' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },
  feat_users: {
    message: "👥 ஊழியர் & பயனர் பாத்திரங்கள்\n\nயார் எதை அணுகலாம் என்பதைக் கட்டுப்படுத்தவும்:\n\n• பாத்திரம் அடிப்படையிலான அணுகல் — சூப்பர் நிர்வாகி, நிர்வாகி, ஊழியர், பயிற்சியாளர், மாணவர்\n• மின்னஞ்சல் வழியாக ஊழியர்களை அழைக்கவும் — அவர்கள் ஏற்று உடனடி அணுகல் பெறுவார்கள்\n• ஒவ்வொரு பாத்திரமும் தேவையானதை மட்டுமே பார்க்கும்\n• தனித்தனி பயனர் ஒதுக்கீடுகளுடன் பல கிளைகளை நிர்வகிக்கவும்",
    options: [
      { label: 'மற்ற அம்சங்கள்', icon: '✨', next: 'features' },
      { label: 'இலவசமாக தொடங்கு', icon: '🚀', next: 'get_started' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },
  feat_dashboard: {
    message: "📊 டாஷ்போர்டு & அறிக்கைகள்\n\nநிகழ்நேர நுண்ணறிவுகளை ஒரு பார்வையில் பெறுங்கள்:\n\n• செயலில் உள்ள மாணவர்கள், வருவாய் மற்றும் சேர்க்கை போக்குகள்\n• தற்போதைய கிளை, ஒட்டுமொத்தம் அல்லது அனைத்து குத்தகைதாரர்கள் மூலம் வடிகட்டவும்\n• தொகுதி நிலை கண்ணோட்டம் மற்றும் சமீபத்திய செயல்பாட்டு ஊட்டம்\n• ஆஃப்லைன் அறிக்கையிடலுக்கு தரவை ஏற்றுமதி செய்யவும்",
    options: [
      { label: 'மற்ற அம்சங்கள்', icon: '✨', next: 'features' },
      { label: 'இலவசமாக தொடங்கு', icon: '🚀', next: 'get_started' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },

  pricing: {
    message: "DriveMynt ஒவ்வொரு பள்ளி அளவிற்கும் நெகிழ்வான திட்டங்களை வழங்குகிறது:\n\n🆓 இலவச சோதனை — 14 நாட்கள், 50 மாணவர்கள், கிரெடிட் கார்டு தேவையில்லை\n⭐ ஸ்டாண்டர்ட் — 50 மாணவர்கள் வரை, 1 கிளை\n🚀 புரொபஷனல் — 500 மாணவர்கள் வரை, பல கிளைகள்\n🏢 எண்டர்பிரைஸ் — வரம்பற்ற மாணவர்கள் & கிளைகள்\n\n💡 வருடாந்திர பில்லிங்கில் 20% வரை சேமிக்கவும்!\n\nசரியான திட்டத்தை தேர்ந்தெடுக்க உதவி வேண்டுமா?",
    options: [
      { label: 'திட்டம் தேர்வு செய்ய உதவு', icon: '🎯', next: 'pricing_recommend' },
      { label: 'இலவச சோதனை விவரங்கள்', icon: '🆓', next: 'free_trial' },
      { label: 'ஸ்டாண்டர்ட் திட்டம்', icon: '⭐', next: 'plan_standard' },
      { label: 'புரொபஷனல் திட்டம்', icon: '🚀', next: 'plan_professional' },
      { label: 'எண்டர்பிரைஸ் திட்டம்', icon: '🏢', next: 'plan_enterprise' },
      { label: 'முழு விலை பக்கம் பார்க்க', icon: '💳', action: 'pricing' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },
  pricing_recommend: {
    message: "சரியான திட்டத்தைக் கண்டறிய உதவுகிறேன்!\n\nஉங்கள் டிரைவிங் பள்ளியில் எத்தனை செயலில் உள்ள மாணவர்கள் உள்ளனர்?",
    options: [
      { label: 'இப்போது தொடங்குகிறேன் (< 50)', icon: '🌱', next: 'rec_small' },
      { label: 'வளர்ந்து வருகிறது (50–500)', icon: '📈', next: 'rec_medium' },
      { label: 'பெரிய பள்ளி (500+)', icon: '🏫', next: 'rec_large' },
      { label: 'பின்', icon: '←', next: 'pricing' },
    ],
  },
  rec_small: {
    message: "சிறிய பள்ளிக்கு (50 மாணவர்களுக்கு கீழ்) நாங்கள் பரிந்துரைக்கிறோம்:\n\n⭐ ஸ்டாண்டர்ட் திட்டம்\n• அனைத்து முக்கிய தொகுதிகளும் அடங்கும்\n• 1 கிளை, 50 மாணவர்கள் வரை\n• மிகவும் மலிவான நுழைவு திட்டம்\n\n💡 முதலில் இலவச சோதனையுடன் தொடங்குங்கள் — கிரெடிட் கார்டு தேவையில்லை!",
    options: [
      { label: 'ஸ்டாண்டர்ட் திட்ட விவரங்கள்', icon: '⭐', next: 'plan_standard' },
      { label: 'இலவச சோதனை தொடங்கு', icon: '🆓', action: 'register' },
      { label: 'விலை பக்கம் பார்க்க', icon: '💳', action: 'pricing' },
      { label: 'பின்', icon: '←', next: 'pricing_recommend' },
    ],
  },
  rec_medium: {
    message: "வளர்ந்து வரும் பள்ளிக்கு (50–500 மாணவர்கள்) நாங்கள் பரிந்துரைக்கிறோம்:\n\n🚀 புரொபஷனல் திட்டம்\n• 500 மாணவர்கள் வரை\n• பல கிளை ஆதரவு\n• மேம்பட்ட பகுப்பாய்வு & ஏற்றுமதி அறிக்கைகள்\n• பயிற்சியாளர் செயல்திறன் கண்காணிப்பு\n\n💡 வருடாந்திர பில்லிங் மாதாந்திரத்தை விட ~20% சேமிக்கும்.",
    options: [
      { label: 'புரொபஷனல் திட்ட விவரங்கள்', icon: '🚀', next: 'plan_professional' },
      { label: 'இலவச சோதனை தொடங்கு', icon: '🆓', action: 'register' },
      { label: 'விலை பக்கம் பார்க்க', icon: '💳', action: 'pricing' },
      { label: 'பின்', icon: '←', next: 'pricing_recommend' },
    ],
  },
  rec_large: {
    message: "பெரிய பள்ளிக்கு (500+ மாணவர்கள்) நாங்கள் பரிந்துரைக்கிறோம்:\n\n🏢 எண்டர்பிரைஸ் திட்டம்\n• வரம்பற்ற மாணவர்கள் & கிளைகள்\n• முன்னுரிமை வாடிக்கையாளர் ஆதரவு\n• பிரத்யேக ஆன்போர்டிங் உதவி\n\n📩 தனிப்பயன் மேற்கோளுக்கு எங்களைத் தொடர்பு கொள்ளவும்.",
    options: [
      { label: 'எண்டர்பிரைஸ் திட்ட விவரங்கள்', icon: '🏢', next: 'plan_enterprise' },
      { label: 'எங்களைத் தொடர்பு கொள்ளவும்', icon: '📞', next: 'contact' },
      { label: 'விலை பக்கம் பார்க்க', icon: '💳', action: 'pricing' },
      { label: 'பின்', icon: '←', next: 'pricing_recommend' },
    ],
  },
  plan_standard: {
    message: "⭐ ஸ்டாண்டர்ட் திட்டம்\n\n✅ மாணவர்கள்: 50 செயலில் உள்ளவர்கள் வரை\n✅ கிளைகள்: 1\n✅ அனைத்து முக்கிய தொகுதிகளுக்கும் முழு அணுகல்\n✅ சேர்க்கை & கட்டண மேலாண்மை\n✅ வாகனம் & தொகுதி திட்டமிடல்\n✅ மின்னஞ்சல் ஆதரவு\n\n💰 மாதாந்திர & வருடாந்திர பில்லிங் கிடைக்கும்\n💡 சிறந்தது: தொடங்கும் சிறிய டிரைவிங் பள்ளிகளுக்கு.",
    options: [
      { label: 'இலவச சோதனை தொடங்கு', icon: '🆓', action: 'register' },
      { label: 'விலை பக்கம் பார்க்க', icon: '💳', action: 'pricing' },
      { label: 'அனைத்து திட்டங்களை ஒப்பிடு', icon: '📊', next: 'pricing' },
      { label: 'பின்', icon: '←', next: 'pricing' },
    ],
  },
  plan_professional: {
    message: "🚀 புரொபஷனல் திட்டம்\n\n✅ மாணவர்கள்: 500 செயலில் உள்ளவர்கள் வரை\n✅ கிளைகள்: பல\n✅ அனைத்து ஸ்டாண்டர்ட் அம்சங்கள் +\n✅ மேம்பட்ட பகுப்பாய்வு & அறிக்கைகள்\n✅ CSV / PDF ஏற்றுமதிகள்\n✅ பல கிளை மேலாண்மை\n✅ பயிற்சியாளர் செயல்திறன் கண்காணிப்பு\n✅ முன்னுரிமை மின்னஞ்சல் ஆதரவு\n\n💰 வருடாந்திர பில்லிங் = ~20% சேமிப்பு",
    options: [
      { label: 'இலவச சோதனை தொடங்கு', icon: '🆓', action: 'register' },
      { label: 'விலை பக்கம் பார்க்க', icon: '💳', action: 'pricing' },
      { label: 'அனைத்து திட்டங்களை ஒப்பிடு', icon: '📊', next: 'pricing' },
      { label: 'பின்', icon: '←', next: 'pricing' },
    ],
  },
  plan_enterprise: {
    message: "🏢 எண்டர்பிரைஸ் திட்டம்\n\n✅ மாணவர்கள்: வரம்பற்ற\n✅ கிளைகள்: வரம்பற்ற\n✅ அனைத்து புரொபஷனல் அம்சங்கள் +\n✅ பிரத்யேக கணக்கு மேலாளர்\n✅ மேம்பட்ட பாத்திர அனுமதிகள்\n✅ ஒயிட்-லேபல் விருப்பங்கள்\n✅ தொலைபேசி & அரட்டை முன்னுரிமை ஆதரவு\n✅ தனிப்பயன் ஆன்போர்டிங் & பயிற்சி",
    options: [
      { label: 'மேற்கோளுக்கு தொடர்பு கொள்ளவும்', icon: '📞', next: 'contact' },
      { label: 'விலை பக்கம் பார்க்க', icon: '💳', action: 'pricing' },
      { label: 'பின்', icon: '←', next: 'pricing' },
    ],
  },

  free_trial: {
    message: "🆓 இலவச சோதனை\n\n✅ காலம்: செயல்படுத்தப்பட்ட நாளிலிருந்து 14 நாட்கள்\n✅ மாணவர்கள்: 50 வரை\n✅ கிளைகள்: 1\n✅ அனைத்து முக்கிய தொகுதிகளும் அடங்கும் — சேர்க்கை, வாகனங்கள், தொகுதிகள், சேவைகள், கணக்கியல்\n✅ கிரெடிட் கார்டு தேவையில்லை\n\n⚠️ சோதனைக்குப் பிறகு உங்கள் தரவு பாதுகாப்பானது — தொடர சந்தா செலுத்தவும்.",
    options: [
      { label: 'இப்போது இலவச சோதனை தொடங்கு', icon: '🚀', action: 'register' },
      { label: 'கட்டண திட்டங்களை ஒப்பிடு', icon: '📊', next: 'pricing' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },

  get_started: {
    message: "DriveMynt-ஐ தொடங்குவது எளிது:\n\n1️⃣ உங்கள் பள்ளியைப் பதிவு செய்யுங்கள் — 2 நிமிடங்கள் ஆகும்\n2️⃣ உங்கள் 14-நாள் இலவச சோதனை தானாகவே தொடங்கும்\n3️⃣ உங்கள் சேவைகள், வாகனங்கள் & ஊழியர்களைச் சேர்க்கவும்\n4️⃣ உடனே மாணவர்களைச் சேர்க்கத் தொடங்குங்கள்!\n\nஅமைப்பு கட்டணம் இல்லை. கிரெடிட் கார்டு தேவையில்லை.",
    options: [
      { label: 'இப்போது பதிவு செய்யுங்கள் — இலவசம்', icon: '🚀', action: 'register' },
      { label: 'ஏற்கனவே கணக்கு உள்ளது', icon: '🔑', action: 'login' },
      { label: 'விலை பற்றி சொல்லுங்கள்', icon: '💰', next: 'pricing' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },

  mobile_app: {
    message: "📱 DriveMynt மொபைல் ஆப்\n\nஆம்! DriveMynt-ல் மாணவர்கள் மற்றும் பயிற்சியாளர்களுக்கான மொபைல் ஆப் உள்ளது:\n\n• மாணவர்கள் தங்கள் அட்டவணையைப் பார்க்கலாம், அமர்வுகளைக் கண்காணிக்கலாம் & சேர்க்கை நிலையைச் சரிபார்க்கலாம்\n• பயிற்சியாளர்கள் பயணத்தின்போது தங்கள் தொகுதிகளை நிர்வகிக்கலாம்\n• சாலையில் அமர்வுகளுக்கு நிகழ்நேர GPS இருப்பிட கண்காணிப்பு\n• Android & iOS-க்கு கிடைக்கும்",
    options: [
      { label: 'தொடங்கு', icon: '🚀', next: 'get_started' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },

  contact: {
    message: "📞 தொடர்பு கொள்ளுங்கள்\n\nஎங்கள் குழு உங்களுக்கு உதவ மகிழ்ச்சியாக இருக்கிறது!\n\n📧 மின்னஞ்சல்: contact@auxwit.com\n\nஅல்லது எங்கள் வலைதளத்தில் உள்ள தொடர்பு படிவத்தைப் பயன்படுத்தவும் — நாங்கள் வழக்கமாக 1 வணிக நாளுக்குள் பதிலளிப்போம்.\n\nவிற்பனை விசாரணைகள் அல்லது டெமோவுக்கு, தொடர்பு கொள்ளுங்கள்!",
    options: [
      { label: 'தொடர்பு படிவத்திற்கு செல்', icon: '✉️', action: 'contact' },
      { label: 'விலையைப் பார்க்க', icon: '💰', next: 'pricing' },
      { label: 'இலவச சோதனை தொடங்கு', icon: '🚀', action: 'register' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },

  faq: {
    message: "அடிக்கடி கேட்கப்படும் கேள்விகள் இங்கே உள்ளன. எந்த கேள்விக்கு பதில் வேண்டும்?",
    options: [
      { label: 'என் தரவு பாதுகாப்பானதா?', icon: '🔒', next: 'faq_security' },
      { label: 'பல கிளைகளை நிர்வகிக்க முடியுமா?', icon: '🌿', next: 'faq_branches' },
      { label: 'என்ன ஆவணங்கள் ஆதரிக்கப்படுகின்றன?', icon: '📄', next: 'faq_documents' },
      { label: 'அறிக்கைகளை ஏற்றுமதி செய்ய முடியுமா?', icon: '📥', next: 'faq_export' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },
  faq_security: {
    message: "🔒 தரவு பாதுகாப்பு\n\nஉங்கள் தரவு முழுமையாக பாதுகாக்கப்படுகிறது:\n\n• தரவு ஓய்வு நிலையிலும் போக்குவரத்திலும் குறியாக்கம் செய்யப்பட்டுள்ளது\n• ஒவ்வொரு பள்ளியின் தரவும் தனிமைப்படுத்தப்பட்டுள்ளது (பல-குத்தகைதாரர் கட்டமைப்பு)\n• வழக்கமான தானியங்கி காப்புப்பிரதிகள்\n• OTP சரிபார்ப்புடன் பாதுகாப்பான அங்கீகாரம்",
    options: [
      { label: 'மேலும் கேள்விகள்', icon: '❓', next: 'faq' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },
  faq_branches: {
    message: "🌿 பல கிளைகள்\n\nஆம்! DriveMynt பல கிளை மேலாண்மையை ஆதரிக்கிறது:\n\n• அமைப்புகளில் கிளை முறையை இயக்கவும்\n• வரம்பற்ற கிளைகளைச் சேர்க்கவும் (புரொபஷனல் & எண்டர்பிரைஸ் திட்டங்கள்)\n• ஒவ்வொரு கிளைக்கும் தனி ஊழியர்கள், வாகனங்கள் & மாணவர்களை ஒதுக்கவும்\n• தலைப்பிலிருந்து கிளைகளுக்கு இடையே மாறவும்\n• அனைத்து கிளைகளிலும் ஒருங்கிணைந்த அறிக்கைகள்",
    options: [
      { label: 'திட்டங்களை பார்க்க', icon: '💰', next: 'pricing' },
      { label: 'மேலும் கேள்விகள்', icon: '❓', next: 'faq' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },
  faq_documents: {
    message: "📄 ஆவண மேலாண்மை\n\nDriveMynt ஒவ்வொரு சேர்க்கைக்கும் தேவையான ஆவணங்களை பதிவேற்றுவதையும் கண்காணிப்பதையும் ஆதரிக்கிறது:\n\n• ஆதார் அட்டை, உரிமம் நகல், பாஸ்போர்ட் புகைப்படம் & மேலும்\n• ஆவணங்கள் ஒவ்வொரு மாணவரின் சேர்க்கையுடன் இணைக்கப்பட்டுள்ளன\n• சரிபார்ப்பின் போது எளிதாக மதிப்பாய்வு செய்யலாம்\n• சேவை/வகை அடிப்படையில் கட்டமைக்கக்கூடியது",
    options: [
      { label: 'மேலும் கேள்விகள்', icon: '❓', next: 'faq' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },
  faq_export: {
    message: "📥 ஏற்றுமதி & அறிக்கைகள்\n\nஆம், DriveMynt தரவு ஏற்றுமதிகளை ஆதரிக்கிறது:\n\n• கட்டண அறிக்கைகளை PDF அல்லது Excel ஆக ஏற்றுமதி செய்யலாம்\n• சேர்க்கை மற்றும் மாணவர் பட்டியல்களை ஏற்றுமதி செய்யலாம்\n• புரொபஷனல் & எண்டர்பிரைஸ் திட்டங்களில் கிடைக்கும்\n• கணக்கியல் அல்லது சேர்க்கை பக்கங்களிலிருந்து நேரடியாக பதிவிறக்கம் செய்யலாம்",
    options: [
      { label: 'திட்டங்களை பார்க்க', icon: '💰', next: 'pricing' },
      { label: 'மேலும் கேள்விகள்', icon: '❓', next: 'faq' },
      { label: 'மெனுவிற்கு திரும்பு', icon: '←', next: 'start' },
    ],
  },
};

const allFlows: Record<ChatbotLang, PublicChatConfig> = {
  en: englishFlows,
  ta: tamilFlows,
};

export const getPublicChatbotFlows = (lang: ChatbotLang): PublicChatConfig => allFlows[lang];

// Default export for backward compatibility
const publicChatbotFlows = englishFlows;
export default publicChatbotFlows;
